(() => {
  class FsPhotoRibbon extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.viewport = this.querySelector('[data-ribbon-viewport]');
      this.group = this.querySelector('[data-ribbon-group]');
      this.button = this.querySelector('[data-ribbon-toggle]');
      if (!this.viewport || !this.group || !this.button) return;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.motion.matches;
      this.hovered = false;
      this.focused = false;
      this.visible = false;
      this.speed = Number(this.dataset.speed) || 60;
      this.position = this.viewport.scrollLeft;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.button.addEventListener('click', () => { this.paused = !this.paused; this.updateButton(); }, options);
      this.addEventListener('mouseenter', () => { this.hovered = true; }, options);
      this.addEventListener('mouseleave', () => { this.hovered = false; }, options);
      this.addEventListener('focusin', () => { this.focused = true; }, options);
      this.addEventListener('focusout', () => { requestAnimationFrame(() => { this.focused = this.contains(document.activeElement); }); }, options);
      this.motion.addEventListener('change', () => { this.paused = this.motion.matches; this.updateButton(); }, options);
      this.viewport.addEventListener('scroll', () => {
        if (this.paused || this.hovered || this.focused || this.drag || this.touching) this.position = this.viewport.scrollLeft;
      }, { ...options, passive: true });
      this.viewport.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.viewport.scrollBy({ left: (event.key === 'ArrowRight' ? 1 : -1) * this.viewport.clientWidth * .6, behavior: this.motion.matches ? 'instant' : 'smooth' });
      }, options);
      this.viewport.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse') { this.touching = true; return; }
        if (event.button !== 0) return;
        this.drag = { x: event.clientX, scroll: this.viewport.scrollLeft };
        this.viewport.setPointerCapture(event.pointerId);
      }, options);
      this.viewport.addEventListener('pointermove', (event) => {
        if (this.drag) this.viewport.scrollLeft = this.drag.scroll + this.drag.x - event.clientX;
      }, options);
      this.viewport.addEventListener('pointerup', () => { this.drag = null; this.touching = false; this.position = this.viewport.scrollLeft; }, options);
      this.viewport.addEventListener('pointercancel', () => { this.drag = null; this.touching = false; this.position = this.viewport.scrollLeft; }, options);
      this.observer = new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting; });
      this.observer.observe(this);
      this.updateButton();
      this.tick = (time) => {
        const elapsed = this.lastTime ? Math.min(time - this.lastTime, 60) : 0;
        this.lastTime = time;
        if (this.visible && !document.hidden && !this.paused && !this.hovered && !this.focused && !this.drag && !this.touching) {
          const width = this.group.getBoundingClientRect().width;
          if (width > 0) {
            this.position = (this.position + elapsed * this.speed / 1000) % width;
            this.viewport.scrollLeft = this.position;
          }
        }
        this.frame = requestAnimationFrame(this.tick);
      };
      this.frame = requestAnimationFrame(this.tick);
    }
    updateButton() {
      this.button.setAttribute('aria-label', this.paused ? 'Play photo strip' : 'Pause photo strip');
      this.button.setAttribute('aria-pressed', String(this.paused));
      this.button.firstElementChild.textContent = this.paused ? '▶' : 'Ⅱ';
    }
    disconnectedCallback() { cancelAnimationFrame(this.frame); this.abort?.abort(); this.observer?.disconnect(); this.ready = false; }
  }
  class FsStoryFilm extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.button = this.querySelector('button');
      if (!this.video || !this.button || this.ready) return;
      this.ready = true;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = this.motion.matches;
      this.video.muted = true;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.button.addEventListener('click', () => {
        this.userPaused = !this.video.paused;
        if (this.video.paused) this.video.play().catch(() => {}); else this.video.pause();
      }, options);
      this.video.addEventListener('play', () => { this.button.setAttribute('aria-label', 'Pause training film'); this.button.textContent = 'Ⅱ'; }, options);
      this.video.addEventListener('pause', () => { this.button.setAttribute('aria-label', 'Play training film'); this.button.textContent = '▶'; }, options);
      this.motion.addEventListener('change', () => { this.userPaused = this.motion.matches; this.sync(); }, options);
      document.addEventListener('visibilitychange', () => this.sync(), options);
      this.observer = new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting; this.sync(); });
      this.observer.observe(this);
    }
    sync() { if (this.visible && !this.userPaused && !document.hidden) this.video.play().catch(() => {}); else this.video.pause(); }
    disconnectedCallback() { this.video?.pause(); this.abort?.abort(); this.observer?.disconnect(); this.ready = false; }
  }
  if (!customElements.get('fs-photo-ribbon')) customElements.define('fs-photo-ribbon', FsPhotoRibbon);
  if (!customElements.get('fs-story-film')) customElements.define('fs-story-film', FsStoryFilm);
})();
